import { Spin, Switch, Table } from 'antd';
import { useAppSelector } from '@/store/hooks';
import { selectApexAssetMap, selectApexLoading } from '@/store/features/apex/apex-selectors';
import { useApexHoldingsAccounts } from '@/hooks/useApexHoldingsAccounts';
import { formatCurrency } from '@wavvest/shared-utils';
import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import * as echarts from 'echarts';
import { useTheme } from '@/contexts/ThemeContext';
import {
  selectPlaidAccounts,
  selectPlaidHoldings,
  selectPlaidLoading,
} from '@/store/features/plaid/plaid-selectors';
// import { getApexAssetMapData } from "@/api/custom";
// import { updateAssetMap } from "@/store/features/apex/apexSliceV2";
import {
  ASSET_LIABILITY,
  generateAssetClassesView,
  getUnclassifiedApexAllocation,
  normalizeApexAssetClass,
} from '@/util/planning-util';
import {
  PlaidHoldingsData,
  PlaidSecurityData,
  PlaidHoldingData,
  isPlaidInvestmentAccount,
  selectPlaidDisplayBalance,
} from '@/util/plaid-util';

type AllocationPieItem = {
  value: number;
  name: string;
  itemStyle: { color: string };
};

type PieTooltipParams = {
  name: string;
  value: number;
  percent: number;
  color: string;
};

type AllocationTableRow = {
  key: string;
  assetClass: string;
  weight: string;
  value: string;
  color: string;
};

const DEFAULT_PIE_CHART_DATA: AllocationPieItem[] = [
  { value: 0, name: 'Equities', itemStyle: { color: '#1656BD' } },
  { value: 0, name: 'Commodities', itemStyle: { color: '#0A94B0' } },
  { value: 0, name: 'Fixed Income', itemStyle: { color: '#14664C' } },
  { value: 0, name: 'Cash', itemStyle: { color: '#00483B' } },
  { value: 0, name: 'Other', itemStyle: { color: '#6B7280' } },
];

// classify holding type to allocation category
// Valid Plaid security types: cash, cryptocurrency, derivative, equity, etf,
// fixed income, loan, mutual fund, other
const classifyHolding = (holdingType?: string, isCashEquivalent?: boolean): string => {
  // Check if this is a cash equivalent first (e.g., money market funds)
  if (isCashEquivalent) {
    return 'Cash';
  }

  const type = (holdingType || '').toLowerCase().trim();
  if (!type) {
    console.warn('Empty holding type - defaulting to Other classification');
    return 'Other';
  }

  switch (type) {
    // Cash, currency, and money market funds
    case 'cash':
      return 'Cash';

    // Domestic and foreign equities
    case 'equity':
      return 'Equities';

    // Multi-asset exchange-traded investment funds
    case 'etf':
      return 'Equities';

    // Bonds and certificates of deposit (CDs)
    case 'fixed income':
      return 'Fixed Income';

    // Loans and loan receivables
    case 'loan':
      return 'Fixed Income';

    // Open- and closed-end vehicles pooling funds of multiple investors
    // Note: These could be equity, fixed income, or mixed - default to Equity
    case 'mutual fund':
      return 'Equities';

    // Digital or virtual currencies
    case 'cryptocurrency':
      return 'Equities'; // Classify crypto as Equity

    // Options, warrants, and other derivative instruments
    case 'derivative':
      return 'Equities'; // Classify derivatives as Equity

    // Unknown or other investment types
    case 'other':
      console.warn(`Plaid returned "other" security type - classifying as Other`);
      return 'Other';

    // Should never reach here with valid Plaid data
    default:
      console.error(
        `Unexpected security type "${holdingType}" - this is not a valid Plaid security type. Defaulting to Other.`,
      );
      return 'Other';
  }
};

// directly process Plaid holdings data and add value by category
const processPlaidHoldingsForAllocation = (
  plaidHoldings: PlaidHoldingsData,
): Record<string, number> => {
  const allocation: Record<string, number> = {
    Equities: 0,
    Commodities: 0,
    'Fixed Income': 0,
    Cash: 0,
    Other: 0,
  };

  if (!plaidHoldings.securities || !Array.isArray(plaidHoldings.securities)) {
    return allocation;
  }

  plaidHoldings.securities.forEach((security: PlaidSecurityData) => {
    if (!security.holdings || !Array.isArray(security.holdings)) return;

    security.holdings.forEach((holding: PlaidHoldingData) => {
      const allocationType = classifyHolding(
        holding.type || security.type,
        security.is_cash_equivalent,
      );
      const value = holding.value || 0;
      if (Object.prototype.hasOwnProperty.call(allocation, allocationType) && value > 0) {
        allocation[allocationType] += value;
      } else if (value > 0) {
        console.error(
          `Plaid holding classified to invalid category "${allocationType}" (type: "${holding.type || security.type}", isCashEquivalent: ${security.is_cash_equivalent}, value: ${value})`,
        );
      }
    });
  });

  return allocation;
};

const AllocationTab: React.FC = () => {
  const { theme } = useTheme();
  const assetMap = useAppSelector(selectApexAssetMap);

  // Legacy per-account Apex accounts for a legacy Apex firm, or lightweight
  // {id, holdings} shims built from firm-data's blended custodian accounts
  // for a firm-data + Apex firm — see useApexHoldingsAccounts.
  const apexAccounts = useApexHoldingsAccounts();
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const plaidHoldings = useAppSelector(selectPlaidHoldings);

  const mainPieChartRef = useRef<HTMLDivElement>(null);
  const mainPieChartInstance = useRef<echarts.ECharts | null>(null);
  const [liquidAssets, setLiquidAssets] = useState(0);

  const loading = useAppSelector(selectApexLoading);
  const plaidLoading = useAppSelector(selectPlaidLoading);

  const [pieChartData, setPieChartData] = useState<AllocationPieItem[]>([]);
  const [isDataGenerated, setIsDataGenerated] = useState(false);
  const [showApexOnly, setShowApexOnly] = useState(false);

  //Toggle to show only Apex accounts only
  const handleToggleApexOnly = useCallback((checked: boolean) => {
    setShowApexOnly(checked);
  }, []);

  const getMainPieOption = useCallback(() => {
    //make the amount text dynamic based on the liquid assets value
    const amountText = formatCurrency(liquidAssets);
    const amountFontSize = amountText.length > 14 ? 24 : amountText.length > 10 ? 28 : 34;

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: PieTooltipParams) => {
          const { name, value, percent, color } = params;
          return `
            <div style="font-weight: bold; margin-bottom: 4px;">${name}</div>
            <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>
              <span>${formatCurrency(value)} (${percent}%)</span>
            </div>
          `;
        },
      },
      legend: { show: false },
      graphic: [
        {
          type: 'group',
          left: 'center',
          top: 'center',
          children: [
            {
              type: 'text',
              style: {
                text: 'Assets',
                textAlign: 'center',
                fill: '#6E6E6E',
                fontSize: 18,
                fontWeight: 400,
              },
              x: 0,
              y: -12,
            },
            {
              type: 'text',
              style: {
                text: amountText,
                textAlign: 'center',
                fill: theme === 'dark' ? '#ffffff' : '#000000',
                fontSize: amountFontSize,
                fontWeight: 600,
                fontFamily: 'Roboto',
              },
              x: 0,
              y: 12,
            },
          ],
        },
      ],
      series: [
        {
          name: 'Asset Allocation',
          type: 'pie',
          radius: ['80%', '95%'],
          emphasis: { label: { show: false } },
          label: { show: false },
          data: pieChartData,
        },
      ],
    };
  }, [pieChartData, liquidAssets, theme]);

  const initMainCharts = useCallback(() => {
    if (mainPieChartRef.current && pieChartData.length > 0) {
      if (mainPieChartInstance.current) {
        mainPieChartInstance.current.dispose();
      }
      mainPieChartInstance.current = echarts.init(mainPieChartRef.current);
      mainPieChartInstance.current.setOption(getMainPieOption());
    }
  }, [pieChartData, getMainPieOption]);

  useEffect(() => {
    if (pieChartData.length > 0) {
      initMainCharts();
    }

    const handleResize = () => {
      if (mainPieChartInstance.current) {
        mainPieChartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mainPieChartInstance.current) {
        mainPieChartInstance.current.dispose();
        mainPieChartInstance.current = null;
      }
    };
  }, [pieChartData, initMainCharts]);

  const { apexDataLoaded, allDataLoaded } = useMemo(() => {
    // Check if Apex holdings are loaded (if there are Apex accounts)
    const apexHoldingsLoaded =
      apexAccounts.length === 0 || apexAccounts.every((account) => account.holdings !== undefined);
    const apexAssetMapLoaded = apexAccounts.length === 0 || Boolean(assetMap);
    const apexDataLoaded = apexHoldingsLoaded && apexAssetMapLoaded;

    // Check if Plaid holdings are loaded (if there are Plaid accounts with investments)
    const hasPlaidInvestmentAccounts = plaidAccounts.some((acc) => isPlaidInvestmentAccount(acc));
    const plaidDataLoaded =
      !hasPlaidInvestmentAccounts || (plaidHoldings !== null && !plaidLoading.holdings);

    return {
      apexDataLoaded,
      plaidDataLoaded,
      allDataLoaded: showApexOnly ? apexDataLoaded : apexDataLoaded && plaidDataLoaded,
    };
  }, [apexAccounts, assetMap, plaidAccounts, plaidHoldings, plaidLoading.holdings, showApexOnly]);

  // cache Plaid data processing result, avoid duplicate calculation
  const plaidAllocation = useMemo(() => {
    const hasHoldings =
      !!plaidHoldings &&
      Array.isArray(plaidHoldings.securities) &&
      plaidHoldings.securities.length > 0;
    const hasPlaidAccounts = Array.isArray(plaidAccounts) && plaidAccounts.length > 0;

    if (!hasHoldings && !hasPlaidAccounts) {
      return null;
    }

    const allocation: Record<string, number> = {
      Equities: 0,
      Commodities: 0,
      'Fixed Income': 0,
      Cash: 0,
      Other: 0,
    };

    if (hasHoldings) {
      const fromHoldings = processPlaidHoldingsForAllocation(plaidHoldings as PlaidHoldingsData);
      Object.entries(fromHoldings).forEach(([k, v]) => {
        if (Object.prototype.hasOwnProperty.call(allocation, k) && v > 0) {
          allocation[k] += v;
        }
      });
    }

    if (hasPlaidAccounts) {
      const cashFromNonInvestmentAccounts = plaidAccounts
        .filter(
          (acc) =>
            acc.asset_or_liability === ASSET_LIABILITY.ASSET && !isPlaidInvestmentAccount(acc),
        )
        .reduce((sum, acc) => {
          const balance = selectPlaidDisplayBalance(acc);
          return sum + (typeof balance === 'number' ? balance : 0);
        }, 0);
      if (cashFromNonInvestmentAccounts > 0) {
        allocation.Cash += cashFromNonInvestmentAccounts;
      }
    }

    return allocation;
  }, [plaidHoldings, plaidAccounts]);

  // cache Apex data processing result, avoid duplicate calculation
  const apexAllocation = useMemo(() => {
    if (apexAccounts.length === 0 || !assetMap || !apexDataLoaded) {
      return null;
    }
    const treeData = generateAssetClassesView(apexAccounts, undefined, assetMap);

    const allocation: Record<string, number> = {
      Equities: 0,
      Commodities: 0,
      'Fixed Income': 0,
      Cash: 0,
      Other: 0,
    };

    if (treeData && treeData.children && treeData.children.length > 0) {
      treeData.children.forEach((child) => {
        const category = normalizeApexAssetClass(child.text);
        if (Object.prototype.hasOwnProperty.call(allocation, category)) {
          allocation[category] = child.valueUsd;
        } else {
          // Log unmatched asset classes and add to Other category
          console.warn(
            `Apex asset class "${child.text}" (value: ${child.valueUsd}) does not match expected allocation categories. Adding to Other.`,
          );
          allocation.Other += child.valueUsd;
        }
      });
    }

    // Add holdings whose symbol is not in the class_tree (buildTree drops them).
    const unclassified = getUnclassifiedApexAllocation(apexAccounts, assetMap);
    Object.keys(allocation).forEach((key) => {
      allocation[key] += unclassified[key];
    });

    return allocation;
  }, [apexAccounts, assetMap, apexDataLoaded]);

  const assetAllocationColumns = [
    {
      title: 'Asset Class',
      dataIndex: 'assetClass',
      key: 'assetClass',
      render: (text: string, record: AllocationTableRow) => {
        const color = record.color || '#ccc';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                height: '10px',
                width: '10px',
                backgroundColor: color,
                borderRadius: '50%',
                display: 'inline-block',
                marginRight: '12px',
              }}
            ></span>
            <span>{text}</span>
          </div>
        );
      },
    },
    { title: 'Weight', dataIndex: 'weight', key: 'weight' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  const generateAssetAllocationData = useCallback(() => {
    // check if there is any data to process
    if (!apexAllocation && !plaidAllocation) {
      const assetMapReady = apexAccounts.length === 0 || Boolean(assetMap);
      if (!allDataLoaded || !assetMapReady) {
        setIsDataGenerated(false);
        return;
      }

      const fallbackPieChartData = DEFAULT_PIE_CHART_DATA.map((item) => ({
        ...item,
        value: 0,
      }));
      setLiquidAssets(0);
      setPieChartData(fallbackPieChartData);
      setIsDataGenerated(true);
      return;
    }

    try {
      // reset defaultPieChartData values
      const newPieChartData = DEFAULT_PIE_CHART_DATA.map((item) => ({
        ...item,
        value: 0,
      }));

      // merge Apex data
      if (apexAllocation) {
        Object.entries(apexAllocation).forEach(([allocationType, value]) => {
          const matchingItem = newPieChartData.find((item) => item.name === allocationType);
          if (matchingItem && value > 0) {
            matchingItem.value += value;
          }
        });
      }

      // merge Plaid data (skip when toggle shows Apex only)
      if (!showApexOnly && plaidAllocation) {
        Object.entries(plaidAllocation).forEach(([allocationType, value]) => {
          const matchingItem = newPieChartData.find((item) => item.name === allocationType);
          if (matchingItem && value > 0) {
            matchingItem.value += value;
          }
        });
      }

      // output summary of final merged result
      const totalValue = newPieChartData.reduce((sum, item) => sum + item.value, 0);

      setLiquidAssets(totalValue);
      setPieChartData(newPieChartData);
      setIsDataGenerated(true);
    } catch (error) {
      console.error('Failed to generate asset allocation data:', error);
    }
  }, [apexAllocation, plaidAllocation, showApexOnly, allDataLoaded, apexAccounts.length, assetMap]);

  useEffect(() => {
    generateAssetAllocationData();
  }, [generateAssetAllocationData]);

  const assetAllocationTableData = useMemo(() => {
    if (pieChartData.length === 0) {
      return DEFAULT_PIE_CHART_DATA.map((item, index) => ({
        key: index.toString(),
        assetClass: item.name,
        weight: '0%',
        value: formatCurrency(0),
        color: item.itemStyle.color,
      }));
    }

    const totalValue = pieChartData.reduce((sum, item) => sum + item.value, 0);

    return pieChartData.map((item, index) => ({
      key: index.toString(),
      assetClass: item.name,
      weight: totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) + '%' : '0%',
      value: formatCurrency(item.value),
      color: item.itemStyle.color,
    }));
  }, [pieChartData]);

  return (
    <div className="asset-allocation-content">
      <div className="asset-allocation-title">Asset Allocation</div>
      {apexAccounts.length > 0 && (
        <div className="asset-allocation-toggle">
          <Switch checked={showApexOnly} onChange={handleToggleApexOnly} size="small" />
          <span className="asset-allocation-toggle-label">Managed accounts only</span>
        </div>
      )}
      <div className="asset-allocation-main">
        {loading.holdings ||
        (!showApexOnly && plaidLoading.holdings) ||
        !isDataGenerated ||
        !allDataLoaded ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              width: '100%',
            }}
          >
            <Spin
              size="large"
              tip={
                loading.holdings && !showApexOnly && plaidLoading.holdings
                  ? 'Loading portfolio and external holdings...'
                  : loading.holdings
                    ? 'Loading portfolio holdings...'
                    : !showApexOnly && plaidLoading.holdings
                      ? 'Loading external holdings...'
                      : 'Loading allocation data...'
              }
            />
          </div>
        ) : (
          <>
            <div className="allocation-table-container">
              <Table
                columns={assetAllocationColumns}
                dataSource={assetAllocationTableData}
                pagination={false}
                size="small"
                rowKey="key"
              />
            </div>
            <div className="allocation-chart-container">
              <div
                ref={mainPieChartRef}
                className="pie-chart"
                style={{ height: '100%', width: '100%', minHeight: '250px' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(AllocationTab);
